import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useFilterQueryState } from 'src/components/MainFilterBar/useFilterQueryState';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { DownloadIcon, IconsFill, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CommandOptionType,
  SaleClient,
  SaleQuery,
} from 'src/WebApiController';

export const useExportSales = ({
  textContentId,
  textOnly,
  iconOnlyMode,
  variant,
  ...props
}: Omit<ComponentProps<typeof ButtonWithIcon>, 'icon' | 'onClick'> & {
  textOnly?: boolean;
}) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { showErrorDialog } = useClientLogContext();
  const { filterQueryWithEventIds } = useFilterQueryState<SaleQuery>(
    ActionOutboxEntityType.Sale
  );
  const [isLoading, setIsLoading] = useState(false);

  const onExportButtonClicked = useCallback(async () => {
    setIsLoading(true);
    await tryInvokeApi(
      async () => {
        const result = await new SaleClient(
          activeAccountWebClientConfig
        ).exportSales(filterQueryWithEventIds);

        if (result.data) {
          const url = URL.createObjectURL(result.data);
          const a = document.createElement('a');
          a.setAttribute('href', url);
          a.setAttribute('download', result.fileName ?? '');
          a.click();
          URL.revokeObjectURL(url);
          a.remove();
        }
      },
      (error) => {
        showErrorDialog('SaleClient.exportSales', error, {
          trackErrorData: { filterQueryWithEventIds },
        });
      },
      () => setIsLoading(false)
    );
  }, [activeAccountWebClientConfig, filterQueryWithEventIds, showErrorDialog]);

  const iconFill = useMemo(
    () =>
      props.disabled
        ? IconsFill.textDisabled
        : iconOnlyMode || (variant ?? 'text') !== 'regular'
          ? IconsFill.textBrand
          : IconsFill.textInverted,
    [iconOnlyMode, props.disabled, variant]
  );

  const openDialogStableRef = useEventCallback(() => {
    onExportButtonClicked();
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.SalesExportSales,
        onSelect: openDialogStableRef,
      },
    });

    return () => {
      unregisterCommand({ commandId: CommandOptionType.SalesExportSales });
    };
  }, [openDialogStableRef, registerCommand, unregisterCommand]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    return [
      {
        title: ContentId.ExportSales,
        onClick: onExportButtonClicked,
        disabled: isLoading,
      },
    ];
  }, [isLoading, onExportButtonClicked]);

  return {
    dropdown: (
      <PosDropdownItem key="ExportSales">
        <ButtonWithIcon
          {...props}
          onClick={onExportButtonClicked}
          disabled={isLoading || props.disabled}
          icon={
            !textOnly ? (
              isLoading ? (
                <RotatingWrapper>
                  <ProcessingIcon fill={iconFill} />
                </RotatingWrapper>
              ) : (
                <DownloadIcon withHoverEffect fill={iconFill} />
              )
            ) : null
          }
          iconOnlyMode={iconOnlyMode}
          variant={variant ?? 'text'}
          textContentId={textContentId || ContentId.Export}
        />
      </PosDropdownItem>
    ),
    actionItems,
  };
};
